import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { globalSearch } from '../lib/api';
import { getChatUnreadCount } from '../lib/api';
import { cn, getInitials, getClientDisplayName } from '../lib/utils';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import NotificationBell from './NotificationBell';
import {
  House, Users, UsersFour, CalendarCheck, ClipboardText, Gear, SignOut,
  MagnifyingGlass, Plus, CaretDown, X, Sliders, UsersThree, TreeStructure, ChatCircleDots, CalendarBlank, Bell,
  CaretLeft, CaretRight, Scroll, TrendDown, CurrencyInr
} from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const navItems = [
  { path: '/', icon: House, label: 'Dashboard' },
  { path: '/leads', icon: UsersFour, label: 'Leads' },
  { path: '/clients', icon: Users, label: 'Clients' },
  { path: '/families', icon: UsersThree, label: 'Families' },
  { path: '/follow-ups', icon: CalendarCheck, label: 'Follow-ups' },
  { path: '/tasks', icon: ClipboardText, label: 'Tasks' },
  { path: '/calendar', icon: CalendarBlank, label: 'Calendar' },
  { path: '/sip-reminders', icon: Bell, label: 'SIP Reminders' },
  { path: '/market-crash', icon: TrendDown, label: 'Market Crash' },
  { path: '/incentives', icon: CurrencyInr, label: 'Incentives' },
  { path: '/team-mapping', icon: Sliders, label: 'Team Mapping', adminOnly: true },
  { path: '/chat', icon: ChatCircleDots, label: 'Chat' },
  { path: '/team', icon: TreeStructure, label: 'Team View' },
];

const adminNavItems = [
  { path: '/admin/users', icon: Gear, label: 'User Management' },
  { path: '/admin/hierarchy', icon: TreeStructure, label: 'Hierarchy' },
  { path: '/admin/audit-log', icon: Scroll, label: 'Audit Log' },
  { path: '/admin/settings', icon: Sliders, label: 'Settings' },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ leads: [], clients: [] });
  const [searching, setSearching] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddTarget, setQuickAddTarget] = useState(null); // { type: 'follow-up'|'task', target: null }
  const [chatUnread, setChatUnread] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', !prev);
      return !prev;
    });
  };

  const visibleNavItems = React.useMemo(() =>
    navItems.filter(item => !item.adminOnly || isAdmin),
    [isAdmin]
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Poll for chat unread count
  useEffect(() => {
    const fetchUnread = () => {
      getChatUnreadCount().then(res => setChatUnread(res.data.unread_count)).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) {
      setSearchResults({ leads: [], clients: [] });
      return;
    }
    setSearching(true);
    try {
      const res = await globalSearch(q);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <TooltipProvider delayDuration={100}>
    <div className="flex h-screen bg-[#FAFAFA]">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="sidebar-overlay md:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn("sidebar flex flex-col", sidebarCollapsed && "collapsed", mobileMenuOpen && "mobile-open")} data-testid="sidebar">
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-zinc-200">
          <Link to="/" className="flex items-center gap-2 overflow-hidden">
            <div className="w-8 h-8 bg-[#002FA7] rounded-sm flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="font-bold text-lg tracking-tight md:hidden block" style={{ fontFamily: 'Manrope' }}>
              Finadore
            </span>
            {!sidebarCollapsed && (
              <span className="font-bold text-lg tracking-tight hidden md:block" style={{ fontFamily: 'Manrope' }}>
                Finadore
              </span>
            )}
          </Link>
          <button
            onClick={toggleSidebar}
            className="p-1 rounded-sm hover:bg-zinc-200 text-zinc-500 transition-colors flex-shrink-0 hidden md:flex"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
          </button>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1 rounded-sm hover:bg-zinc-200 text-zinc-500 transition-colors flex-shrink-0 md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1">
            {visibleNavItems.map((item) => (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <Link
                    to={item.path}
                    className={cn(
                      'nav-item',
                      location.pathname === item.path && 'active'
                    )}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <item.icon size={20} weight={location.pathname === item.path ? 'fill' : 'regular'} className="flex-shrink-0" />
                    <span className="md:hidden">{item.label}</span>
                    {!sidebarCollapsed && <span className="hidden md:inline">{item.label}</span>}
                    {!sidebarCollapsed && item.label === 'Chat' && chatUnread > 0 && (
                      <span className="ml-auto bg-[#002FA7] text-white text-[10px] font-bold h-5 min-w-[20px] flex items-center justify-center rounded-full" data-testid="chat-unread-badge">
                        {chatUnread}
                      </span>
                    )}
                    {sidebarCollapsed && item.label === 'Chat' && chatUnread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#002FA7] text-white text-[9px] font-bold h-4 min-w-[16px] flex items-center justify-center rounded-full">
                        {chatUnread}
                      </span>
                    )}
                  </Link>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right" className="text-xs">
                    {item.label}
                  </TooltipContent>
                )}
              </Tooltip>
            ))}
          </div>

          {isAdmin && (
            <>
              <div className={cn("my-4 border-t border-zinc-200", sidebarCollapsed ? "mx-2" : "mx-4")} />
              {!sidebarCollapsed && (
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold px-4 mb-2">
                  Admin
                </p>
              )}
              <div className="space-y-1">
                {adminNavItems.map((item) => (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.path}
                        className={cn(
                          'nav-item',
                          location.pathname === item.path && 'active'
                        )}
                        data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon size={20} weight={location.pathname === item.path ? 'fill' : 'regular'} className="flex-shrink-0" />
                        <span className="md:hidden">{item.label}</span>
                        {!sidebarCollapsed && <span className="hidden md:inline">{item.label}</span>}
                      </Link>
                    </TooltipTrigger>
                    {sidebarCollapsed && (
                      <TooltipContent side="right" className="text-xs">
                        {item.label}
                      </TooltipContent>
                    )}
                  </Tooltip>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* User */}
        <div className="p-2 border-t border-zinc-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={cn("w-full flex items-center gap-3 p-2 rounded-sm hover:bg-zinc-100 transition-colors", sidebarCollapsed && "md:justify-center")} data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarFallback className="bg-[#002FA7] text-white text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left overflow-hidden md:hidden">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                </div>
                {!sidebarCollapsed && (
                  <>
                    <div className="flex-1 text-left overflow-hidden hidden md:block">
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-xs text-zinc-500 capitalize">{user?.roles?.length ? user.roles.map(r => r === 'account_manager' ? 'AM' : r === 'operations_manager' ? 'OM' : r).join(', ') : user?.role}</p>
                    </div>
                    <CaretDown size={16} className="text-zinc-400 flex-shrink-0 hidden md:block" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <SignOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="top-header flex items-center justify-between px-6 gap-3" data-testid="top-header">
          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden p-2 -ml-2 rounded-sm hover:bg-zinc-100 text-zinc-600"
            data-testid="mobile-menu-btn"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>

          {/* Search */}
          <div className="flex-1 max-w-md hidden md:block">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Search leads, clients by name, phone, PAN..."
                className="pl-10 rounded-sm bg-zinc-50 border-zinc-200"
                onClick={() => setShowSearch(true)}
                readOnly
                data-testid="global-search-trigger"
              />
            </div>
          </div>

          {/* Notification Bell */}
          <button onClick={() => setShowSearch(true)} className="md:hidden p-2 rounded-sm hover:bg-zinc-100 text-zinc-500">
            <MagnifyingGlass size={20} />
          </button>
          <NotificationBell />

          {/* Quick Add */}
          <DropdownMenu open={showQuickAdd} onOpenChange={setShowQuickAdd}>
            <DropdownMenuTrigger asChild>
              <Button className="bg-[#002FA7] hover:bg-[#00227A] text-white rounded-sm" data-testid="quick-add-button">
                <Plus size={18} className="md:mr-2" />
                <span className="hidden md:inline">Quick Add</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => { setShowQuickAdd(false); navigate('/leads/new'); }}>
                <UsersFour size={16} className="mr-2" /> New Lead
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowQuickAdd(false); navigate('/clients/new'); }}>
                <Users size={16} className="mr-2" /> New Client
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setShowQuickAdd(false); setQuickAddTarget({ type: 'follow-up' }); }}>
                <CalendarCheck size={16} className="mr-2" /> New Follow-up
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowQuickAdd(false); setQuickAddTarget({ type: 'task' }); }}>
                <ClipboardText size={16} className="mr-2" /> New Task
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShowQuickAdd(false); navigate('/activities/new'); }}>
                <CalendarCheck size={16} className="mr-2" /> New Activity
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page Content */}
        <main className="main-content">
          {children}
        </main>
      </div>

      {/* Search Modal */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Global Search</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="Search by name, phone, email, or PAN..."
                className="pl-10 rounded-sm"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
                data-testid="global-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchResults({ leads: [], clients: [] }); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {searching ? (
              <div className="mt-4 space-y-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {searchResults.leads.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-2">Leads</p>
                    <div className="space-y-1">
                      {searchResults.leads.map(lead => (
                        <Link
                          key={lead.id}
                          to={`/leads/${lead.id}`}
                          onClick={() => setShowSearch(false)}
                          className="flex items-center justify-between p-3 rounded-sm hover:bg-zinc-50 transition-colors"
                        >
                          <div>
                            <p className="font-medium text-sm">{lead.full_name}</p>
                            <p className="text-xs text-zinc-500">{lead.phone} • {lead.email}</p>
                          </div>
                          <Badge className="badge-new text-xs">Lead</Badge>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {searchResults.clients.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.1em] font-semibold text-zinc-500 mb-2">Clients</p>
                    <div className="space-y-1">
                      {searchResults.clients.map(client => (
                        <Link
                          key={client.id}
                          to={`/clients/${client.id}`}
                          onClick={() => setShowSearch(false)}
                          className="flex items-center justify-between p-3 rounded-sm hover:bg-zinc-50 transition-colors"
                        >
                          <div>
                            <p className="font-medium text-sm">{getClientDisplayName(client)}</p>
                            <p className="text-xs text-zinc-500">{client.phone} • {client.pan_number}</p>
                          </div>
                          <Badge className="badge-active text-xs">Client</Badge>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {searchQuery.length >= 2 && searchResults.leads.length === 0 && searchResults.clients.length === 0 && (
                  <p className="text-center text-zinc-500 py-8">No results found for "{searchQuery}"</p>
                )}

                {searchQuery.length < 2 && (
                  <p className="text-center text-zinc-400 py-8 text-sm">Type at least 2 characters to search</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Quick Add Target Picker Dialog */}
      <Dialog open={!!quickAddTarget} onOpenChange={() => setQuickAddTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Create {quickAddTarget?.type === 'follow-up' ? 'Follow-up' : 'Task'} for...
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              variant="outline"
              className="h-16 justify-start gap-4 rounded-sm hover:border-[#002FA7] hover:bg-blue-50/50"
              onClick={() => {
                const path = quickAddTarget?.type === 'follow-up' ? '/follow-ups/new?for=client' : '/tasks/new?for=client';
                setQuickAddTarget(null);
                navigate(path);
              }}
              data-testid="quick-add-for-client"
            >
              <Users size={24} className="text-[#002FA7]" />
              <div className="text-left">
                <p className="font-medium">Client</p>
                <p className="text-xs text-zinc-500">Existing client in the system</p>
              </div>
            </Button>
            <Button
              variant="outline"
              className="h-16 justify-start gap-4 rounded-sm hover:border-amber-500 hover:bg-amber-50/50"
              onClick={() => {
                const path = quickAddTarget?.type === 'follow-up' ? '/follow-ups/new?for=lead' : '/tasks/new?for=lead';
                setQuickAddTarget(null);
                navigate(path);
              }}
              data-testid="quick-add-for-lead"
            >
              <UsersFour size={24} className="text-amber-600" />
              <div className="text-left">
                <p className="font-medium">Lead</p>
                <p className="text-xs text-zinc-500">Prospective lead</p>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
